import { useCallback, useEffect, useState } from "react";
import ds from "../services/persistence.service";
import { useStore } from "../contexts/dataStore.context";
import { isObject } from "wombat-global/src/utilities";
import { tryJSON } from "wombat-global/src/utilities/object.utilities";

function hasStoredValue<T>(value: unknown): value is { value: T } {
  return isObject(value) && Object.hasOwn(value, "value");
}

function getMigratedLocalStorageValue<T>(key: string): T | undefined {
  const storedValue = tryJSON(localStorage.getItem(key));

  if (!hasStoredValue<T>(storedValue)) {
    return undefined;
  }

  return storedValue.value;
}

export function usePersistenceStorage<T = unknown>(
  key: string,
  initialValue: T,
  options?: {
    debug?: boolean;
    removeOnLogout?: boolean;
  },
): [T | undefined | typeof initialValue, (val: T | ((v: T) => T)) => void] {
  type FuncT = (v: T) => T;

  const [keys, setKeys] = useStore<Record<string, unknown>>(
    "persistentStorageKeyMap",
  );
  const [_value, setLocalState] = useState<T | undefined>(initialValue);

  useEffect(() => {
    if (options?.removeOnLogout && !keys[key]) {
      setKeys({ [key]: true }, { merge: true });
    }
  }, [options?.removeOnLogout, keys, key, setKeys]);

  useEffect(() => {
    let hasChanged = false;
    const unSub = ds.on((x) => {
      if (x.key === key) {
        if (x.type === "delete") {
          hasChanged = true;
          setLocalState(undefined);
        }

        if (x.type === "change" && _value !== x.value) {
          hasChanged = true;
          setLocalState(x.value as T);
        }

        if (x.type === "change" && _value === x.value) {
          options?.debug &&
            // oxlint-disable-next-line no-console
            console.info(
              "usePersistenceStorage: event update ignored values equal",
              key,
            );
        }
      }
    });

    ds.get<T>(key).then((d) => {
      if (!hasChanged) {
        /**
         * MIGRATE - remove this after AUG 1st 2026
         */
        if (d === undefined || d === null) {
          const migratedValue = getMigratedLocalStorageValue<T>(key);

          if (migratedValue !== undefined) {
            void ds.set(key, migratedValue);
            localStorage.removeItem(key);
            setLocalState(migratedValue);
            return;
          }
        }

        setLocalState(d ?? initialValue);
      }
    });

    return () => {
      unSub && unSub();
    };
    // oxlint-disable-next-line exhaustive-deps
  }, [key]);

  const changeHandler = useCallback(
    function innerChangeHandler(val: T | FuncT) {
      if (typeof val == "function") {
        setLocalState((v) => {
          const next = (val as FuncT)(v as T);
          ds.set(key, next as unknown);
          return next;
        });
      } else {
        setLocalState(val);
        ds.set(key, val as unknown);
      }
    },
    [key],
  );

  return [_value, changeHandler];
}
